// The Type tab: the product's type (ProductTypeField over the product-type tree). The type decides how the
// product behaves — its page, its delivery, the editor's tabs (its profile, API catalog lib/product-profiles) —
// and that stays behind it: nobody picks or reads it here. The type may be changed at any time (a type picked
// by mistake is put right here); one that behaves differently asks first, saying what may change.
import React, { useEffect, useState } from "react"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { Field, errorMessage } from "../../../AdminShared/ui"
import { ProductTypeInfo, ProductTypeSet } from "../../../AdminShared/api"
import ProductTypeField from "../../../AdminShared/product-types/ProductTypeField"
import { useProductTypes, refreshProductTypes } from "../../../AdminShared/product-types/useProductTypes"
import { noticeSentForReview } from "../shared/review-notice"
import usePortalTexts from "../../../Vendor/texts/usePortalTexts"
import { PRODUCTS } from "../../../Vendor/texts/bundles"

export default function TypeTab({ editor, pid }) {
  const t = usePortalTexts(PRODUCTS)
  const { read, adopt } = editor
  const { bySlug } = useProductTypes()
  const [info, setInfo] = useState(null)
  const [busy, setBusy] = useState(false)
  // the product's profile, from its type in the tree (the raw load answers it)
  const contentId = read("profile")?.id || ""

  useEffect(() => {
    if (!pid) return
    ProductTypeInfo(pid).then((res) => !errorMessage(res) && setInfo(res))
  }, [pid])

  const apply = async (payload) => {
    setBusy(true)
    try {
      const res = await ProductTypeSet(pid, payload)
      const failed = errorMessage(res)
      if (failed) return showNotification({ message: failed, className: "bg-admin_error text-white" })
      setInfo(res)
      if (payload.proposal) refreshProductTypes()
      // another type for a product on sale: back to review
      noticeSentForReview(res, t, editor)
      adopt("productType", res.productType || "")
      if (!res.contentTypeChanged) return
      // the editor shows what was saved (the answer carries the product's profile): its tabs follow at once
      adopt("profile", res.profile || { id: res.contentType })
    } finally {
      setBusy(false)
    }
  }

  // a type that behaves differently: said before it is done
  const choose = (payload, nextId) => {
    if (!nextId || !contentId || nextId === contentId) return apply(payload)
    showPromptDialog({
      title: t`Change the product type?`,
      message: t`This type is sold differently: the product's page, delivery and editor tabs change with it. What was filled in that no longer applies is kept, but may not show.`,
      confirmText: t`Change`,
      cancelText: t`Cancel`,
      onConfirm: () => apply(payload),
    })
  }

  return (
    <div className="flex flex-col gap-5 max-w-[760px]" data-testid="type-tab">
      <Field label={t`Product type`} hint={!info?.productType && contentId ? t`Pick what the product is: its page, delivery and tabs follow the type.` : undefined}>
        {info && (
          <ProductTypeField
            key={info.productType || "none"}
            value={info.productType || ""}
            t={t}
            disabled={busy}
            onChange={({ slug, node }) => choose({ slug }, node?.effectiveProfile || "")}
            onPropose={(proposal) => choose({ proposal }, bySlug.get(proposal.parentSlug)?.effectiveProfile || "")}
          />
        )}
      </Field>
    </div>
  )
}
